import React, { useEffect, useId, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { RJSFSchema } from '@rjsf/utils'
import {
  CalendarIcon,
  SaveChangesBanner,
  AppLayout,
  useGetPayloadSchemaByIdQuery,
  useUpdatePayloadSchemaMutation,
  useGetContractsQuery,
  Mtds,
  XIcon,
  CheckIcon,
  countApprovedSchemaCombinations,
  countSchemaCombinations,
  CodeIcon,
  ConfirmDeletionModal,
  CopySimpleIcon,
  IntersectThreeIcon,
  PencilSimpleIcon,
  ScreenHeader,
  ModalDialog,
  type ScreenHeaderProperty,
  toLocaleDateString,
  TrashIcon,
  UserIcon,
  useDeletePayloadSchemaMutation,
  useErrorToast,
  type PayloadSchema
} from '../index'
import { mainWidgets } from '../schemas/main-widgets'
import { SchemaBlockEditor } from '../components/schema/SchemaBlockEditor'
import { SchemaCodeEditor } from '../components/schema/SchemaCodeEditor'
import { SchemaPreview } from '../components/schema/SchemaPreview'
import { SchemaUiEditor } from '../components/schema/SchemaUiEditor'
import { EditSchemaModal } from '../components/edit-schema-modal/EditSchemaModal'
import { CopySchemaModal } from '../components/copy-schema-modal/CopySchemaModal'

type SchemaScreenProps = {
  topBar?: React.ReactNode
  sidebar?: React.ReactNode
  useMobileBar?: boolean
}

type SaveWarnings = {
  affectedCount: number
}

type SchemaScreenHeaderProps = {
  schema: PayloadSchema
  isCodeView: boolean
  setIsCodeView: (isActive: boolean) => void
  contractId: string | undefined
  combinationCount?: number
  approvedCombinationCount?: number
}

const SchemaScreenHeader = ({
  schema,
  isCodeView,
  setIsCodeView,
  contractId,
  combinationCount,
  approvedCombinationCount
}: SchemaScreenHeaderProps) => {
  const [isOpenEditModal, setOpenEditModal] = useState(false)
  const [isOpenCopyModal, setOpenCopyModal] = useState(false)
  const [isOpenDeleteModal, setOpenDeleteModal] = useState(false)
  const contextMenuId = useId()
  const navigate = useNavigate()
  const deletePayloadSchema = useDeletePayloadSchemaMutation()
  const showDeleteError = useErrorToast('slette skjemaet')
  const properties: ScreenHeaderProperty[] = [
    {
      label: 'Sist oppdatert',
      icon: <CalendarIcon aria-hidden />,
      value: toLocaleDateString(schema.lastUpdated)
    },
    {
      label: 'Opprettet av',
      icon: <UserIcon aria-hidden />,
      value: schema.ownerName ?? '-'
    },
    {
      label: 'Kombinasjoner',
      icon: <IntersectThreeIcon aria-hidden />,
      value: (
        <>
          {combinationCount ?? '-'}
          {approvedCombinationCount != null && (
            <Mtds.Muted> ({approvedCombinationCount} godkjent)</Mtds.Muted>
          )}
        </>
      )
    }
  ]

  return (
    <>
      <ScreenHeader
        title={schema.name}
        actions={
          <>
            <Mtds.Button
              aria-label='Handlingsmeny'
              popoverTarget={contextMenuId}
            />
            <Mtds.Popover as='menu' id={contextMenuId}>
              <li>
                <Mtds.Button onClick={() => setOpenEditModal(true)}>
                  <PencilSimpleIcon />
                  Gi nytt navn
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button onClick={() => setIsCodeView(!isCodeView)}>
                  <CodeIcon />
                  {isCodeView ? 'Deaktiver kodevisning' : 'Aktiver kodevisning'}
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button onClick={() => setOpenCopyModal(true)}>
                  <CopySimpleIcon />
                  Kopier
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button
                  data-color='danger'
                  onClick={() => setOpenDeleteModal(true)}
                >
                  <TrashIcon />
                  Slett
                </Mtds.Button>
              </li>
            </Mtds.Popover>
          </>
        }
        properties={properties}
      />
      <EditSchemaModal
        isOpen={isOpenEditModal}
        setOpen={setOpenEditModal}
        schema={schema}
      />
      <CopySchemaModal
        isOpen={isOpenCopyModal}
        setOpen={setOpenCopyModal}
        schema={schema}
        contractId={contractId}
      />
      <ConfirmDeletionModal
        title={`Skjemaet «${schema.name}» vil bli permanent slettet.`}
        isOpen={isOpenDeleteModal}
        setOpen={setOpenDeleteModal}
        isPending={deletePayloadSchema.isPending}
        onConfirm={async () => {
          if (schema != null) {
            try {
              await deletePayloadSchema.mutateAsync(schema.id)
              Mtds.toast.success('Skjemaet er slettet')
              const navigateTo =
                contractId == null
                  ? '/schemas'
                  : `/contracts/${contractId}?tab=1`
              navigate(navigateTo)
            } catch (error) {
              showDeleteError(error)
            }
          }
        }}
      />
    </>
  )
}

export const SchemaScreen = ({
  topBar,
  sidebar,
  useMobileBar
}: SchemaScreenProps) => {
  const { payloadSchemaId, contractId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const viewParam = searchParams.get('view')
  const {
    data: payloadSchema,
    isPending: isPendingPayloadSchema,
    isError: isErrorPayloadSchema,
    isSuccess: isSuccessPayloadSchema
  } = useGetPayloadSchemaByIdQuery(Number(payloadSchemaId))
  const { data: contracts } = useGetContractsQuery()
  const [schema, setSchema] = useState<RJSFSchema>({})
  const [editorValue, setEditorValue] = useState<string | undefined>()
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false)
  const [isSchemaError, setIsSchemaError] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [saveWarnings, setSaveWarnings] = useState<SaveWarnings | null>(null)
  const updatePayloadSchema = useUpdatePayloadSchemaMutation()
  const [isCodeView, setIsCodeView] = useState<boolean>(viewParam === 'code')
  const showSaveError = useErrorToast('lagre endringene i skjemaet')

  useEffect(() => {
    if (isCodeView) {
      setSearchParams({ view: 'code' }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
  }, [isCodeView])
  useEffect(() => {
    if (isSuccessPayloadSchema) {
      setEditorValue(payloadSchema?.schema)
    }
  }, [isSuccessPayloadSchema, payloadSchema])

  useEffect(() => {
    if (payloadSchema != null && editorValue != null) {
      updateSchemaPreview(editorValue)
      if (editorValue === payloadSchema.schema) {
        setIsUnsavedChanges(false)
      } else {
        setIsUnsavedChanges(true)
      }
    }
  }, [editorValue, payloadSchema])

  const performSave = () => {
    if (payloadSchema != null && editorValue != null) {
      updatePayloadSchema
        .mutateAsync({
          payloadSchemaId: Number(payloadSchemaId),
          etag: payloadSchema.etag,
          body: {
            schema: editorValue,
            etag: payloadSchema.etag,
            name: payloadSchema.name,
            labId: payloadSchema.labId ?? undefined
          }
        })
        .then(() => {
          Mtds.toast.success('Endringer lagret')
          setIsUnsavedChanges(false)
          setIsConfirmDialogOpen(false)
        })
        .catch((error) => {
          showSaveError(error)
        })
    } else {
      Mtds.toast.danger('Kunne ikke lagre endringer')
    }
  }

  const saveChanges = () => {
    if (payloadSchema == null || editorValue == null) {
      Mtds.toast.danger('Kunne ikke lagre endringer')
      return
    }

    if (editorValue !== payloadSchema.schema) {
      const affectedCount =
        contracts == null
          ? 0
          : countApprovedSchemaCombinations(contracts, Number(payloadSchemaId))

      if (affectedCount > 0) {
        setSaveWarnings({ affectedCount })
        setIsConfirmDialogOpen(true)
        return
      }
    }

    performSave()
  }

  const discardChanges = (): void => {
    setEditorValue(payloadSchema?.schema)
  }

  const updateSchemaPreview = (value: string | undefined) => {
    try {
      const parsedValue: RJSFSchema = value ? JSON.parse(value) : {}
      setSchema(parsedValue)
      setIsSchemaError(false)
    } catch {
      setIsSchemaError(true)
    }
  }

  return (
    <AppLayout
      sidebar={sidebar}
      isLoading={isPendingPayloadSchema}
      isError={isErrorPayloadSchema}
      topBar={topBar}
      useMobileBar={useMobileBar}
      isFullWidth={true}
    >
      {isSuccessPayloadSchema && (
        <>
          <SchemaScreenHeader
            schema={payloadSchema}
            isCodeView={isCodeView}
            setIsCodeView={setIsCodeView}
            contractId={contractId}
            combinationCount={
              contracts == null
                ? undefined
                : countSchemaCombinations(contracts, Number(payloadSchemaId))
            }
            approvedCombinationCount={
              contracts == null
                ? undefined
                : countApprovedSchemaCombinations(
                    contracts,
                    Number(payloadSchemaId)
                  )
            }
          />
          <Mtds.Prose>
            <Mtds.Heading as='h2' data-size={'sm'}>
              Bygg skjema
            </Mtds.Heading>
            <p>
              Legg til de elementene du ønsker at registreringsskjemaet skal
              inneholde. Husk at det skal være enklest mulig for inspektøren.
            </p>
          </Mtds.Prose>
          <Mtds.Flex data-items='500'>
            <Mtds.Group
              as={Mtds.Grid}
              data-gap='6'
              data-align-content={'start'}
            >
              <Mtds.Grid>
                <Mtds.Heading as='h4'>Elementer</Mtds.Heading>
                {!isCodeView && (
                  <SchemaBlockEditor
                    value={editorValue}
                    onChange={setEditorValue}
                    contextWidgets={mainWidgets(undefined, false)}
                  />
                )}
                {isCodeView && (
                  <SchemaCodeEditor
                    value={editorValue}
                    onChange={setEditorValue}
                  />
                )}
              </Mtds.Grid>

              {isCodeView && (
                <>
                  <Mtds.Divider />
                  <Mtds.Grid>
                    <Mtds.Heading as='h4'>UiSchema (read-only)</Mtds.Heading>
                    <Mtds.Card>
                      <SchemaUiEditor schema={schema} />
                    </Mtds.Card>
                  </Mtds.Grid>
                </>
              )}
            </Mtds.Group>
            <Mtds.Group
              as={Mtds.Grid}
              data-gap='6'
              data-align-content={'start'}
            >
              <SchemaPreview schema={schema} isCodeView={isCodeView} />
            </Mtds.Group>
          </Mtds.Flex>
        </>
      )}
      {isUnsavedChanges && (
        <SaveChangesBanner
          onSave={saveChanges}
          onDiscard={discardChanges}
          isError={isSchemaError}
        />
      )}
      {saveWarnings != null && (
        <ModalDialog
          id='confirm-schema-save-dialog'
          closedby='any'
          open={isConfirmDialogOpen}
          onClose={() => setIsConfirmDialogOpen(false)}
        >
          <Mtds.Button
            command='close'
            commandfor='confirm-schema-save-dialog'
            aria-label='Lukk'
          />
          <Mtds.Prose>
            <Mtds.Heading>Bekreft lagring av skjema</Mtds.Heading>
            <Mtds.Alert data-color='warning'>
              Dette skjemaet brukes av{' '}
              <strong>
                {saveWarnings.affectedCount}{' '}
                {saveWarnings.affectedCount === 1
                  ? 'kombinasjon'
                  : 'kombinasjoner'}
              </strong>{' '}
              som allerede er godkjent av laboratoriet og kan bli påvirket av
              endringene. Vurder om det heller bør lages et nytt skjema med de
              aktuelle endringene.
            </Mtds.Alert>
            <Mtds.Flex style={{ paddingTop: 'var(--mtds-8)' }}>
              <Mtds.Button
                command='close'
                commandfor='confirm-schema-save-dialog'
              >
                <XIcon />
                Avbryt
              </Mtds.Button>
              <Mtds.Button
                data-variant='primary'
                onClick={performSave}
                aria-busy={updatePayloadSchema.isPending}
                disabled={updatePayloadSchema.isPending}
              >
                <CheckIcon />
                Lagre uansett
              </Mtds.Button>
            </Mtds.Flex>
          </Mtds.Prose>
        </ModalDialog>
      )}
    </AppLayout>
  )
}
